import React from "react";
import { HeaderState } from "../../types";

interface PreviewModalProps {
	state: HeaderState;
	onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ state, onClose }) => {
	// Generate container styles
	const getContainerStyle = (): React.CSSProperties => {
		const baseStyle = {
			...state.styles,
			width: "100%",
		};

		// Add sticky styles if enabled
		if (state.isSticky) {
			return {
				...baseStyle,
				position: "sticky" as "sticky",
				top: 0,
				zIndex: 1000,
			};
		}

		return baseStyle;
	};

	// Generate inner container styles
	const getInnerContainerStyle = (): React.CSSProperties => {
		if (state.containerType === "container") {
			return {
				width: "100%",
				maxWidth: "1200px",
				margin: "0 auto",
				padding: "0 16px",
			};
		}
		return {};
	};

	// Get layout styles
	const getLayoutStyle = (): React.CSSProperties => {
		switch (state.layout) {
			case "standard":
				return {
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap" as "wrap",
					gap: "16px",
					width: "100%",
				};
			case "centered":
				return {
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					flexWrap: "wrap" as "wrap",
					gap: "16px",
					width: "100%",
				};
			case "split":
				return {
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					flexWrap: "wrap" as "wrap",
					width: "100%",
				};
			case "stacked":
				return {
					display: "flex",
					flexDirection: "column" as "column",
					alignItems: "center",
					gap: "16px",
					width: "100%",
				};
			default:
				return {
					display: "flex",
					alignItems: "center",
					flexWrap: "wrap" as "wrap",
					gap: "16px",
					width: "100%",
				};
		}
	};

	// Simple component placeholder rendering based on type
	const renderComponentPlaceholder = (component: any) => {
		// Use simple placeholders for preview
		switch (component.type) {
			case "logo":
				return (
					<div key={component.id} style={{ padding: "8px" }}>
						<img
							src={component.settings.image}
							alt="Logo"
							style={{ maxWidth: "120px", height: "auto" }}
						/>
					</div>
				);
			case "searchBar":
				if (component.settings.size === "full") {
					return (
						<div
							key={component.id}
							style={{
								display: "flex",
								alignItems: "center",
								border: "1px solid #e5e7eb",
								borderRadius: "0.375rem",
								padding: "8px 12px",
								width: component.styles.width || "250px",
								...component.styles,
							}}>
							<span
								style={{
									color: "#9ca3af",
									marginRight: "8px",
								}}>
								<svg
									width="20"
									height="20"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round">
									<circle cx="11" cy="11" r="8"></circle>
									<line
										x1="21"
										y1="21"
										x2="16.65"
										y2="16.65"></line>
								</svg>
							</span>
							<input
								type="text"
								placeholder={component.settings.placeholder}
								style={{
									border: "none",
									outline: "none",
									background: "transparent",
									width: "100%",
								}}
								readOnly
							/>
						</div>
					);
				} else {
					return (
						<button
							key={component.id}
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								padding: "8px",
								background: "transparent",
								border: "none",
							}}>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round">
								<circle cx="11" cy="11" r="8"></circle>
								<line
									x1="21"
									y1="21"
									x2="16.65"
									y2="16.65"></line>
							</svg>
						</button>
					);
				}
			case "navItems":
				return (
					<div
						key={component.id}
						style={{
							display: "flex",
							alignItems: "center",
							gap: component.styles.gap || "16px",
							fontWeight:
								component.settings.fontWeight || "normal",
							textDecoration:
								component.settings.textDecoration || "none",
							fontStyle: component.settings.fontStyle || "normal",
						}}>
						{component.settings.items.map((item: any) => (
							<a
								key={item.id}
								href="#"
								style={{
									color: "inherit",
									textDecoration: "none",
								}}>
								{item.text}
							</a>
						))}
					</div>
				);
			case "wishlist":
			case "cart":
			case "user":
			case "menu":
				// Get button style based on shape
				const getButtonStyle = () => {
					const baseStyle: React.CSSProperties = {
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "8px",
						background:
							component.settings.background || "transparent",
						border: "none",
						color: "#4b5563",
					};

					// Apply button shape
					switch (component.settings.shape) {
						case "box":
							return {
								...baseStyle,
								borderRadius: "0",
							};
						case "circle":
							return {
								...baseStyle,
								borderRadius: "50%",
							};
						case "rounded":
						default:
							return {
								...baseStyle,
								borderRadius: `${
									component.settings.borderRadius || 4
								}px`,
							};
					}
				};

				return (
					<button key={component.id} style={getButtonStyle()}>
						{getIconSVG(component.settings.icon || component.type)}
					</button>
				);
			default:
				return <div key={component.id}>Unknown Component</div>;
		}
	};

	// Helper function to get icon SVG
	const getIconSVG = (iconType: string) => {
		switch (iconType) {
			case "search":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="11" cy="11" r="8"></circle>
						<line x1="21" y1="21" x2="16.65" y2="16.65"></line>
					</svg>
				);
			case "heart":
			case "wishlist":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
					</svg>
				);
			case "cart":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="9" cy="21" r="1"></circle>
						<circle cx="20" cy="21" r="1"></circle>
						<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
					</svg>
				);
			case "user":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
						<circle cx="12" cy="7" r="4"></circle>
					</svg>
				);
			case "menu":
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<line x1="3" y1="12" x2="21" y2="12"></line>
						<line x1="3" y1="6" x2="21" y2="6"></line>
						<line x1="3" y1="18" x2="21" y2="18"></line>
					</svg>
				);
			default:
				return (
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="1.5"
						strokeLinecap="round"
						strokeLinejoin="round">
						<circle cx="12" cy="12" r="10"></circle>
					</svg>
				);
		}
	};

	// Function to render appropriate layout
	const renderLayout = () => {
		const { layout, components } = state;

		switch (layout) {
			case "standard":
				return (
					<div style={getLayoutStyle()}>
						{components.map(renderComponentPlaceholder)}
					</div>
				);
			case "centered":
				return (
					<div style={getLayoutStyle()}>
						{components.map(renderComponentPlaceholder)}
					</div>
				);
			case "split":
				const logo = components.find((c) => c.type === "logo");
				const searchBar = components.find(
					(c) => c.type === "searchBar"
				);
				const otherComponents = components.filter(
					(c) => c.type !== "logo" && c.type !== "searchBar"
				);

				return (
					<div style={getLayoutStyle()}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "16px",
							}}>
							{logo && renderComponentPlaceholder(logo)}
							{searchBar && renderComponentPlaceholder(searchBar)}
						</div>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "16px",
							}}>
							{otherComponents.map(renderComponentPlaceholder)}
						</div>
					</div>
				);
			case "stacked":
				const logoStacked = components.find((c) => c.type === "logo");
				const otherStacked = components.filter(
					(c) => c.type !== "logo"
				);

				return (
					<div style={getLayoutStyle()}>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								width: "100%",
								padding: "8px 0",
							}}>
							{logoStacked &&
								renderComponentPlaceholder(logoStacked)}
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								flexWrap: "wrap",
								gap: "16px",
								width: "100%",
							}}>
							{otherStacked.map(renderComponentPlaceholder)}
						</div>
					</div>
				);
			default:
				return (
					<div style={getLayoutStyle()}>
						{components.map(renderComponentPlaceholder)}
					</div>
				);
		}
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg p-4 max-w-5xl w-full max-h-[90vh] overflow-auto">
				<div className="flex justify-between items-center mb-4">
					<h2 className="text-xl font-bold">Header Preview</h2>
					<button
						className="p-1 rounded-full hover:bg-gray-100"
						onClick={onClose}>
						❌
					</button>
				</div>

				<div className="mb-4 p-4 border rounded">
					<div style={getContainerStyle()}>
						{state.containerType === "container" ? (
							<div style={getInnerContainerStyle()}>
								{renderLayout()}
							</div>
						) : (
							renderLayout()
						)}
					</div>
				</div>

				<div className="border-t pt-4 mt-4">
					<p className="text-gray-600 text-sm mb-2">
						This is how your header will look when published. This
						preview doesn't include interactive features like hover
						effects.
					</p>
					<div className="flex justify-end">
						<button
							className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
							onClick={onClose}>
							Close Preview
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PreviewModal;
